import React, { useState, useRef, useEffect } from 'react';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';
import toast from 'react-hot-toast';

import { X } from 'lucide-react';

import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { createTestRun, updateTestRun, getTestRun } from '../../api/controlTestRuns';
import { MODULE_KEYS } from '../../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from '../shared/ModuleAccessDeniedDialog.tsx';

import TestSetupStep   from './steps/TestSetupStep';
import PopulationStep from './steps/PopulationStep';

function resumeStep(run) {
  if (!run) return 1;
  // No population step for runs that test from evidence files.
  if (run.population_status === 'not_required') return 1;
  const status = (run.testStatus || run.status || '').toLowerCase();
  if (status === 'population_configured' || status === 'population_validated') return 2;
  if (status === 'period_selected') return 2;
  if (run.selected_periods) return 2;
  return 1;
}

function buildResumedPeriod(run) {
  if (!run?.selected_periods) return null;
  const sp = run.selected_periods;
  return {
    frequency:              sp.frequency,
    year:                   sp.year,
    periods:                sp.periods || [],
    selectedDates:          sp.selected_dates,
    isEventBased:           sp.is_event_based,
    isRollforward:          run.is_rollforward,
    testing_period_start:   sp.periods?.[0]?.start,
    testing_period_end:     sp.periods?.[sp.periods.length - 1]?.end,
  };
}

/**
 * TestRunWizard — 2-step wizard for the attribute-based (new) test run flow.
 *
 *   Step 1 — TestSetupStep:  test objective, testing method, sampling, period
 *   Step 2 — PopulationStep: population file + IPE verify + sample selection
 *            (skipped when population is not required — samples are then the evidence
 *            files uploaded after the run is created)
 *
 * onCreated({ testingMethod }) — called after each save so parent can refresh
 *                                and route to the correct post-wizard experience.
 */
export default function TestRunWizard({ control, controlUuid, onClose, onCreated, resumeRun = null }) {
  const { selectedAuditYearId } = useAuditYear();
  const initialStep         = resumeStep(resumeRun);
  const initialTestType     = resumeRun?.test_type       || 'toe';
  const initialTestingMethod = resumeRun?.testing_method || 'holistic';
  const initialSamplingMethod = resumeRun?.sampling_method || 'statistical';
  const resumedPeriod       = buildResumedPeriod(resumeRun);
  const nameEditedRef       = useRef(!!resumeRun?.test_run_name?.trim());

  const [step,           setStep]           = useState(initialStep);
  const [testType,       setTestType]       = useState(initialTestType);
  const [testingMethod,  setTestingMethod]  = useState(initialTestingMethod);
  const [samplingMethod, setSamplingMethod] = useState(initialSamplingMethod);
  const [periodData,     setPeriodData]     = useState(resumedPeriod);
  const [executionId,    setExecutionId]    = useState(resumeRun?.executionId || null);
  const [creating,       setCreating]       = useState(false);
  const [fullRunData,    setFullRunData]    = useState(null);
  const [fullRunFailed,  setFullRunFailed]  = useState(false);
  const {
    checkAccess: checkTestExecutionsModuleAccess,
    dialogProps: testExecutionsModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint:
      'Saving a test run configuration requires the Test Executions module. Contact your organization administrator if you need access.',
  });

  const [testName, setTestName] = useState(() => {
    if (resumeRun?.test_run_name?.trim()) return resumeRun.test_run_name.trim();
    return '';
  });

  // Fetch full run so population_data.items is available for re-selection
  useEffect(() => {
    if (!resumeRun?.executionId) return;
    getTestRun(resumeRun.executionId)
      .then(d => setFullRunData(d))
      .catch(err => {
        console.warn('[TestRunWizard] full run fetch failed — restore will use list data:', err?.message || err);
        setFullRunFailed(true);
      });
  }, [resumeRun?.executionId]);

  // getTestRun returns { run, attributes } — merge run.population (file details)
  // with run.population_data (sample list) so the wizard restore gets both.
  const resolvedInitialPopulation = fullRunData
    ? { ...(fullRunData.run?.population || {}), ...(fullRunData.run?.population_data || {}) }
    : resumeRun?.population_data;

  // The runs list no longer carries population rows (there can be tens of thousands per
  // run), so they come from this fetch. Saving Step 2 before it arrives would replace the
  // stored population with just the selected samples — hold Step 2 until it has loaded.
  const waitingForFullRun = !!resumeRun?.executionId && !fullRunData && !fullRunFailed;

  const requireTestName = () => {
    const trimmed = testName?.trim();
    if (!trimmed) {
      toast.error('Test name is required. Enter a name to identify this test run.');
      return null;
    }
    return trimmed;
  };

  // ── Step 1 complete — create (or update) the run then move to step 2 ──────
  const handleSetupComplete = async ({ testType: t, testingMethod: tm, samplingMethod: sm, periodData: pd, populationStatus: ps = 'provided' }) => {
    if (!checkTestExecutionsModuleAccess()) return;
    const name = requireTestName();
    if (!name) return;

    setTestType(t);
    setTestingMethod(tm);
    setSamplingMethod(sm);
    setPeriodData(pd);
    setCreating(true);

    const fields = {
      test_run_name:        name,
      test_type:            t,
      testing_method:       tm,
      sampling_method:      sm,
      testing_period_start: pd.testing_period_start || pd.periods?.[0]?.start || null,
      testing_period_end:   pd.testing_period_end   || pd.periods?.[pd.periods.length - 1]?.end || null,
      selected_periods: {
        frequency:      pd.frequency,
        year:           pd.year,
        periods:        pd.periods,
        selected_dates: pd.selectedDates,
        is_event_based: pd.isEventBased,
      },
      is_rollforward:   !!pd.isRollforward,
      population_status: ps,
      // Without a population there is nothing more to configure here.
      workflow_status:  ps === 'not_required' ? 'population_configured' : 'period_selected',
    };

    try {
      let id = executionId;
      if (id) {
        await updateTestRun(id, fields);
      } else {
        const resp = await createTestRun({ sox_control_id: controlUuid, control_id: control?.control_id, ...fields, audit_year_id: selectedAuditYearId || undefined });
        id = resp.id;
        setExecutionId(id);
      }
      if (ps === 'not_required') {
        toast.success('Test run created. Add evidence files and group them into samples to continue.');
        onCreated?.({ testingMethod: tm, runId: id });
        onClose();
        return;
      }
      setStep(2);
    } catch (err) {
      toast.error('Failed to save test run: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  // ── Step 2 complete — save population + samples then close wizard ─────────
  const handlePopulationComplete = async (data) => {
    if (!checkTestExecutionsModuleAccess()) return;
    const name = requireTestName();
    if (!name) return;

    if (!executionId) {
      toast.error('Complete Step 1 first.');
      setStep(1);
      return;
    }

    setCreating(true);
    try {
      const pop = data.populationData || {};
      const selectedRefs = (pop.selected_samples || [])
        .map(ref => typeof ref === 'string' ? ref.trim() : String(ref?.record_ref ?? ref).trim())
        .filter(Boolean);

      if (selectedRefs.length === 0) {
        toast.error('Select at least one sample before saving.');
        setCreating(false);
        return;
      }

      const sm      = data.samplingMethod || samplingMethod || 'statistical';
      const samples = selectedRefs.map(record_ref => ({ record_ref, selection_method: sm }));

      const populationItems = (pop.items?.length ? pop.items : selectedRefs)
        .map(item => ({ record_ref: typeof item === 'string' ? item.trim() : String(item?.record_ref ?? item).trim() }))
        .filter(it => it.record_ref)
        .map(it => (pop.item_rows?.[it.record_ref] ? { ...it, row_data: pop.item_rows[it.record_ref] } : it));

      await updateTestRun(executionId, {
        test_run_name:   name,
        workflow_status: 'population_configured',
        ipe_validated:   data.ipeValidated,
        sampling_method: sm,
        population: {
          source_type:            pop.source === 'file_upload' || pop.items?.length ? 'file_upload' : 'manual_count',
          total_count:            data.totalPopulation ?? pop.total_count ?? null,
          identifier_column:      pop.identifier_column || pop.file_name || 'ID',
          items:                  populationItems,
          population_file_id:     pop.population_file_id     || null,
          population_file_name:   pop.file_name              || null,
          sample_labels:          pop.sample_labels          || null,
          ipe_proof_file_id:      pop.ipe_proof_file_id      || null,
          ipe_proof_file_name:    pop.ipe_proof_file_name    || null,
          ipe_source_note:        pop.ipe_source_note        || null,
          ...(pop.filtered_total_amount        != null ? { filtered_total_amount:        pop.filtered_total_amount        } : {}),
          ...(pop.independent_total_amount     != null ? { independent_total_amount:     pop.independent_total_amount     } : {}),
          ...(pop.ipe_comparison_note          ?         { ipe_comparison_note:          pop.ipe_comparison_note          } : {}),
          ...(pop.ipe_population_record_count  != null ? { ipe_population_record_count:  pop.ipe_population_record_count  } : {}),
          ...(pop.ipe_proof_record_count       != null ? { ipe_proof_record_count:       pop.ipe_proof_record_count       } : {}),
          ...(pop.ipe_population_column_count  != null ? { ipe_population_column_count:  pop.ipe_population_column_count  } : {}),
          ...(pop.ipe_proof_column_count       != null ? { ipe_proof_column_count:       pop.ipe_proof_column_count       } : {}),
          ...(pop.parse_metadata                         ? { parse_metadata:               pop.parse_metadata               } : {}),
        },
        samples,
      });

      // Fire IPE comparison in the background ONLY when the user did NOT already
      // run it interactively in the wizard.  If ipeValidated is true,
      // HandleIPEVerify already saved the result to control_populations — firing
      // again here would overwrite it with potentially stale stored file IDs.
      if (pop.population_file_id && pop.ipe_proof_file_id && !data.ipeValidated) {
        axiosInstance.post(`/api/control-test-runs/${executionId}/population/ipe-verify`, {
          population_file_id: pop.population_file_id,
          ipe_proof_file_id:  pop.ipe_proof_file_id,
          source_note:        pop.ipe_source_note || '',
        }).catch((ipeErr) => {
          if (ipeErr?.response?.status === 403) {
            toast.error('IPE verification failed: ' + resolve403ErrorMessage(ipeErr, ipeErr.response?.data?.error || ipeErr.message));
          }
        });
      }

      onCreated?.({ testingMethod, runId: executionId });
      onClose();
    } catch (err) {
      toast.error('Failed to save population: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900">
            {resumeRun ? `Configure Test Run (RUN-${resumeRun.test_run_number ?? ''})` : 'Create New Test Run'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Test name */}
        <div className="px-5 py-3 border-b border-gray-100 shrink-0">
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Test Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={testName}
            onChange={e => { nameEditedRef.current = true; setTestName(e.target.value); }}
            placeholder="e.g. CTL_000018 · Q1 2026 · Operating Effectiveness (TOE)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>


        {/* Step content */}
        <div className="flex-1 min-h-0 overflow-y-auto text-sm">
          {step === 1 && (
            <TestSetupStep
              control={control}
              onComplete={handleSetupComplete}
              initialTestType={testType}
              initialTestingMethod={testingMethod}
              initialSamplingMethod={samplingMethod}
              initialPeriodData={periodData}
              initialPopulationStatus={resumeRun?.population_status || null}
            />
          )}
          {step === 2 && waitingForFullRun && (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500" role="status">
              <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Loading population…
            </div>
          )}
          {step === 2 && !waitingForFullRun && (
            <PopulationStep
              key={fullRunData ? 'full' : 'partial'}
              periodData={periodData}
              control={control}
              controlUuid={controlUuid}
              runId={executionId}
              testType={testType}
              testingMethod={testingMethod}
              initialPopulation={resolvedInitialPopulation}
              initialSamplingMethod={samplingMethod}
              onComplete={handlePopulationComplete}
              onBack={() => setStep(1)}
            />
          )}

        </div>

        {creating && (
          <div className="px-5 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-2 shrink-0">
            <div className="h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            Saving…
          </div>
        )}
        </div>
      </div>
      <ModuleAccessDeniedDialog {...testExecutionsModuleAccessDialogProps} />
    </>
  );
}
